import styled from '@emotion/styled';
import { useRef, useState } from 'react';
import { Text, Modal, Button, tokens, IconButton, Loader } from '@hotjar/hotsauce';
import { RETAKER_MODAL_HEADER_HEIGHT, RETAKER_BUTTON_BOTTOM_POSITION } from './constants';
import { RecordingsRetakerPlayer } from './RecordingsRetakerPlayer';
import { RetakerHelpModal } from './RetakerHelpModal';

const StyledModal = styled(Modal)`
  min-height: unset;
  max-height: unset;
  min-width: unset;
  max-width: unset;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
`;

const ModalHeader = styled.div`
  width: 100%;
  height: ${RETAKER_MODAL_HEADER_HEIGHT};
  background-color: ${tokens.BRAND_COLORS.neutral_900};
  color: ${tokens.color.text_default_inverse};
  display: flex;
  align-items: center;
  padding: 0 ${tokens.spacer.x1pt5};
`;

const ModalBody = styled(Modal.Body)`
  margin: 0;
  padding: 0;
  flex: 1;
`;

const StyledButton = styled(Button)`
  display: block;
  position: fixed;
  bottom: ${RETAKER_BUTTON_BOTTOM_POSITION};
  right: ${tokens.spacer.x2};
`;

const StyledIconButton = styled(IconButton)`
  margin-right: ${tokens.spacer.x1};

  svg {
    color: ${tokens.color.text_default_inverse};
  }
`;

const HowItWorksContainer = styled.div`
  margin-left: auto;

  svg,
  span {
    color: ${tokens.color.text_default_inverse};
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  background-color: ${tokens.color.white};
  position: fixed;
  top: ${RETAKER_MODAL_HEADER_HEIGHT};
  left: 0;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const HELP_MODAL_VIEWED_KEY = 'HJ_RECORDINGS_RETAKER_HELP_MODAL_VIEWED';

export interface RecordingsRetakerModalProps {
  isOpen: boolean;
  onCancelOrClose: () => void;
  onRetakeScreenshot: (selectedTimeInMS: number) => void;
  recordingId: number | null | undefined; // Need to type it this way for compatibility with types coming from the API
  recordingStartTime: number | null | undefined; // Need to type it this way for compatibility with types coming from the API
}

export const RecordingsRetakerModal = ({
  isOpen,
  onCancelOrClose,
  onRetakeScreenshot,
  recordingId,
  recordingStartTime,
}: RecordingsRetakerModalProps): JSX.Element | null => {
  const currentTime = useRef<number>(recordingStartTime ?? 0);
  const [isPlayerLoaded, setIsPlayerLoaded] = useState(false);
  const [isHelpModalOpen, setHelpModalOpen] = useState(
    !localStorage.getItem(HELP_MODAL_VIEWED_KEY),
  );

  function handleHelpModalClose() {
    setHelpModalOpen(false);
    localStorage.setItem(HELP_MODAL_VIEWED_KEY, 'true');
  }

  const setCurrentTime = (newTimeInMS = 0) => {
    currentTime.current = newTimeInMS;
    if (!isPlayerLoaded) {
      setIsPlayerLoaded(true);
    }
  };

  if (!recordingId || !recordingStartTime) {
    return null;
  }

  function handleCancelOrClose() {
    setIsPlayerLoaded(false);
    onCancelOrClose();
  }

  function handleTakeScreenshot() {
    setIsPlayerLoaded(false);
    onRetakeScreenshot(currentTime.current);
  }

  return (
    <StyledModal
      size="s"
      isOpen={isOpen}
      onClose={handleCancelOrClose}
      contentLabel="Screenshot recordings retaker"
      hideCloseButton
    >
      <ModalBody>
        <ModalHeader>
          <StyledIconButton
            description="Back"
            icon="chevron_left"
            size="s"
            tooltipText="Back"
            kind="ghost"
            onClick={handleCancelOrClose}
          />
          <Text as="span" textType="subtitle2" textColor="text_default_inverse">
            Use the timeline to find the right frame, then take a new screenshot
          </Text>
          <HowItWorksContainer>
            <Button icon="help" kind="ghost" onClick={() => setHelpModalOpen(true)}>
              How it works
            </Button>
          </HowItWorksContainer>
        </ModalHeader>
        <RetakerHelpModal isOpen={isHelpModalOpen} onClose={handleHelpModalClose} />
        <RecordingsRetakerPlayer
          recordingId={recordingId}
          recordingStartTime={recordingStartTime}
          setCurrentTime={setCurrentTime}
          setIsLoaded={(isLoaded) => setIsPlayerLoaded(isLoaded)}
        />
        {!isPlayerLoaded && (
          <LoaderContainer>
            <Loader size="xl" />
          </LoaderContainer>
        )}
        {isPlayerLoaded && (
          <StyledButton onClick={handleTakeScreenshot} icon="camera">
            Take new screenshot
          </StyledButton>
        )}
      </ModalBody>
    </StyledModal>
  );
};
